"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import {
  LuTestTubes,
  LuCircleDot,
  LuDna,
  LuAtom,
  LuTrendingUp,
  LuLeaf,
  LuSyringe,
  LuSettings2,
  LuFactory,
  LuHandshake,
} from "react-icons/lu";
import { faqs } from "./faq-data";

const gold = "oklch(0.52 0.12 70)";
const goldBg = "oklch(0.52 0.12 70 / 0.1)";

const productIcons: Record<string, React.ReactNode> = {
  peptides: <LuTestTubes size={22} />,
  exosomes: <LuCircleDot size={22} />,
  "stem-cells": <LuDna size={22} />,
  nad: <LuAtom size={22} />,
  "growth-factors": <LuTrendingUp size={22} />,
  botanicals: <LuLeaf size={22} />,
  "delivery-systems": <LuSyringe size={22} />,
  "custom-formulation": <LuSettings2 size={22} />,
};

const products = [
  {
    slug: "peptides",
    title: "Peptides",
    desc: "BPC-157, TB-500, TB-4, CJC-1295, GHK-Cu, Epithalon, MOTS-c, and custom-synthesis compounds. cGMP certified. COA, HPLC, and mass spec on every lot.",
    meta: "Research · Compounding · Custom Synthesis",
    street: "$35–$75/vial",
    save: "Typical BioChain Saving: 38–65%+",
    links: [
      {
        label: "Peptide Sciences - Research Benchmark",
        href: "https://www.peptidesciences.com",
      },
      {
        label: "Core Peptides - US Supplier Comp",
        href: "https://corepeptides.com",
      },
    ],
  },
  {
    slug: "exosomes",
    title: "Exosomes",
    desc: "MSC-derived, platelet-rich, and plant-based exosome carriers. IV-grade and topical. NTA particle sizing and endotoxin testing on every lot.",
    meta: "MSC-Derived · IV-Grade · NTA Verified",
    street: "$1,200–$3,500/vial",
    save: "Typical BioChain Saving: 30–55%+",
    links: [
      {
        label: "Kimera Labs - Documented US Supplier",
        href: "https://kimeralabs.com",
      },
      {
        label: "Direct Biologics - Clinical Trial Validated",
        href: "https://directbiologics.com",
      },
    ],
  },
  {
    slug: "stem-cells",
    title: "Stem Cell Products",
    desc: "Umbilical cord MSCs, Wharton's jelly, amniotic membrane, adipose-derived concentrates. AATB-accredited tissue banks only, full donor traceability.",
    meta: "MSC · Wharton's Jelly · AATB Accredited",
    street: "$2,000–$8,000/unit",
    save: "Typical BioChain Saving: 25–50%+",
    links: [
      {
        label: "BioLife Solutions - AATB Gold Standard",
        href: "https://www.biolifesolutions.com",
      },
      {
        label: "Vitro Biopharma - MSC Expansion",
        href: "https://www.vitrobiopharma.com",
      },
    ],
  },
  {
    slug: "nad",
    title: "NAD+ & Longevity Compounds",
    desc: "NMN, NR, NAD+ precursors, Spermidine, MOTS-c, Tesamorelin. Pharmaceutical-grade sourcing from cGMP manufacturers - verified against the same API grade retail brands resell.",
    meta: "NAD+ · NMN · Longevity Stack",
    street: "$800–$2,500/kg",
    save: "Typical BioChain Saving: 40–60%+",
    links: [
      {
        label: "Renue By Science - Retail Benchmark",
        href: "https://renuebyscience.com",
      },
      {
        label: "Alive By Nature - Market Rate Comp",
        href: "https://alivebynature.com",
      },
    ],
  },
  {
    slug: "growth-factors",
    title: "Growth Factors & Cytokines",
    desc: "IGF-1, EGF, FGF, PDGF, TGF-β, and proprietary blends. Full cytokine profile validation, endotoxin testing, and cold-chain logistics included.",
    meta: "IGF-1 · EGF · Clinical + Research Grade",
    street: "$80–$220/unit",
    save: "Typical BioChain Saving: 40–60%+",
    links: [],
  },
  {
    slug: "botanicals",
    title: "Botanical & Adaptogenic APIs",
    desc: "Lion's Mane, Ashwagandha KSM-66, Bacopa, Rhodiola, and other pharmaceutical-grade adaptogen actives, third-party tested.",
    meta: "Adaptogens · Nootropics · Research Compounds",
    street: "$180–$400/kg",
    save: "Typical BioChain Saving: 45–60%+",
    links: [],
  },
  {
    slug: "delivery-systems",
    title: "Biologic Devices & Delivery Systems",
    desc: "Microneedle patches, liposomal encapsulation systems, IV bag systems, and reconstitution kits - medical-device-grade consumables sourced alongside your biologics.",
    meta: "Delivery Systems · Medical-Device-Grade",
    street: "$180–$380/kit",
    save: "Typical BioChain Saving: 45–63%+",
    links: [],
  },
  {
    slug: "custom-formulation",
    title: "Custom Formulation & White-Label",
    desc: "Concept-to-finished-product: custom synthesis, proprietary blends, private-label manufacturing. Every supplier negotiation includes MOQ, exclusivity, and quality-guarantee clauses.",
    meta: "Custom Synthesis · White Label · OEM",
    street: "Varies by spec",
    save: "Typical BioChain Saving: 45–70%+",
    links: [
      {
        label: "Blue Sky Peptide - Market Rate Comp",
        href: "https://blueskypeptide.com",
      },
      {
        label: "Amino Asylum - Market Rate Comp",
        href: "https://aminoasylum.shop",
      },
    ],
  },
];

const clientCharacteristics = [
  "Annual biologics spend of $50,000 minimum - sweet spot $150,000–$2M+",
  "Currently purchasing through retail or distribution channels and suspect they are overpaying",
  "Have experienced quality inconsistency or documentation gaps from current suppliers",
  "Scaling fast enough that supplier relationships need to be structured, not transactional",
  "Value verified provenance and chain-of-custody as much as price",
  "Have a purchasing decision-maker who can move in 30–60 days",
];

const clientOrgTypes = [
  "Longevity Clinics",
  "Compounding Pharmacy Networks",
  "Regenerative Medicine Brands",
  "Biotech & Nutraceutical Companies",
  "Medical Spas & Aesthetic Chains",
  "Research Institutions",
  "Wellness Resorts & Integrative Health Centers",
];

const exampleClient = {
  account: "Pacific Longevity Partners",
  type: "Multi-location longevity clinic group - 3 locations, Southern California",
  spend: "$380,000",
  categories: "Peptides, MSC exosomes, NAD+ IV kits, NMN bulk",
  problem:
    "Purchasing through two distributors and one direct manufacturer. No unified COA standard. Exosome particle counts unverified. NMN sourced at retail pricing with no volume commitment.",
  outcome:
    "41% average reduction across evaluated categories. Supplier consolidation from 3 to 1 primary. First-time exclusivity window on top peptide SKU. Elimination of 2 documentation gaps carrying regulatory exposure.",
};

const supplierStrengthVariables = [
  { variable: "GMP Certification Status", weight: "15%" },
  {
    variable:
      "COA Completeness (HPLC, mass spec, endotoxin, heavy metals, microbial)",
    weight: "15%",
  },
  { variable: "Third-Party Testing Independence", weight: "12%" },
  { variable: "Cold-Chain Infrastructure", weight: "10%" },
  { variable: "Lot Traceability", weight: "10%" },
  { variable: "Volume Capacity at Your Scale", weight: "10%" },
  { variable: "Reorder Reliability (12-month on-time rate)", weight: "8%" },
  {
    variable: "Regulatory History (warning letters, recall history)",
    weight: "8%",
  },
  {
    variable: "Contract Flexibility (MOQ, exclusivity, quality guarantee)",
    weight: "7%",
  },
];

const exampleSupplier = {
  name: "Pacific Biologics Supply Co. (illustrative)",
  category: "MSC-Derived Exosomes - IV-Grade",
  score: "87/100",
  certs:
    "FDA-registered facility · cGTP compliant · Dual-lab COA verified · NTA particle-count verified",
  capacity: "Up to 500 vials/month at 60B particle count",
  history: "Zero warning letters · Zero recalls · 4-year operating history",
  posture:
    "Open to exclusivity windows for accounts above $200K annually · Quality guarantee clause available · Lot reservation rights negotiable at 90-day commit",
  why: "This supplier is in active growth mode and needs anchor accounts in new geographies. An account committing $150K+ annually with 90-day purchase orders qualifies for distributor-tier pricing. Fit determines access to that tier.",
};

const vettingPipeline = [
  {
    step: "01",
    label: "Supplier Applies",
    desc: "A quick first-touch application - company, product line, certifications. No uploads, 2-3 minutes.",
  },
  {
    step: "02",
    label: "Scored",
    desc: "Every application is scored against our 12-variable qualification matrix - not accepted on reputation alone.",
  },
  {
    step: "03",
    label: "COA / Chain-of-Custody Verified",
    desc: "Documentation, lab reports, and provenance records are independently checked against the submitted claims.",
  },
  {
    step: "04",
    label: "Approved",
    desc: "Cleared suppliers join the verified BioChain network and become eligible for buyer introductions.",
  },
];

const process = [
  {
    num: "01",
    title: "Supplier Qualification Matrix",
    desc: "12-variable scoring: GMP status, COA completeness, HPLC purity standards, endotoxin testing, NTA particle sizing, cold-chain infrastructure, and real buyer feedback. No supplier enters the network on reputation alone.",
  },
  {
    num: "02",
    title: "BioChain SPY Index",
    desc: "Live market pricing across our vetted manufacturer network - per milligram, per vial, per million particles. What comparable buyers actually paid, updated from closed transactions.",
  },
  {
    num: "03",
    title: "Provenance & Chain-of-Custody",
    desc: "Every order ships with verified documentation: donor tissue consent records, lot traceability, third-party lab reports, and customs compliance paperwork.",
  },
  {
    num: "04",
    title: "Negotiation & Contract Architecture",
    desc: "Volume pricing, exclusivity windows, quality-guarantee clauses, and reorder SLAs negotiated on your behalf - structured to protect you as you scale.",
  },
];

const proofStats = [
  { value: "114+", label: "Compounds Sourced" },
  { value: "13", label: "Supplier Categories" },
  { value: "300+", label: "Vetted Suppliers" },
  { value: "$10B+", label: "Transactions Architected" },
];

const blogPosts = [
  {
    tag: "Regenerative Medicine",
    title: "Peptides, Ibogaine, and the Healing Economy",
    excerpt:
      "Essays on plant medicine, ibogaine therapy, and why the healing economy demands a different kind of supply chain intelligence.",
    href: "https://tonygreenberg.com/blog",
    img: "/biochain/blog-regenerative-medicine.jpg",
  },
  {
    tag: "Human OS 2.0",
    title: "Biohacking, Biomarkers, and the Longevity Protocol",
    excerpt:
      "The personal operating system for health and longevity that informs every BioChain sourcing decision.",
    href: "https://tonygreenberg.com/humanos",
    img: "/biochain/blog-humanos.jpg",
  },
  {
    tag: "Plant Medicine",
    title: "Psychedelic Readiness Index",
    excerpt:
      "A framework for assessing readiness for plant medicine - and why sourcing the compounds matters as much as the protocol.",
    href: "https://tonygreenberg.com/psychedelic-readiness-index",
    img: "/biochain/blog-psychedelic-readiness.jpg",
  },
  {
    tag: "Conscious Capital",
    title: "A Living Declaration: Regenerative vs. Extractive",
    excerpt:
      "Why regenerative vs. extractive is the defining business choice of the next decade - and how BioChain takes a side.",
    href: "https://tonygreenberg.com/living-declaration",
    img: "/biochain/blog-living-declaration.jpg",
  },
];

const testimonials = [
  {
    quote:
      "RampRate's sourcing methodology gave us confidence we had never had with our peptide suppliers. They found us a cGMP manufacturer with better documentation - and we stopped guessing about what was in the vial.",
    attr: "Director of Operations · Longevity Clinic",
  },
  {
    quote:
      "We had no idea our exosome supplier's particle counts were off. RampRate's supplier audit caught it before we used the product clinically.",
    attr: "Founder · Regenerative Medicine Practice",
  },
  {
    quote:
      "The same rigor they brought to our data center negotiation, they brought to our biologics supply chain. Different industry. Same intelligence engine.",
    attr: "VP Operations · BioTech Brand",
  },
];

function Reveal({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.07 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "none" : "translateY(20px)",
        transition: "opacity .55s ease, transform .55s ease",
      }}
    >
      {children}
    </div>
  );
}

export default function BioChainSourcingPage() {
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  return (
    <main>
      {/* Hero */}
      <section className="relative pt-44 pb-20 overflow-hidden section-light">
        <div className="glass-orb glass-orb-amber w-[400px] h-[400px] -top-40 -right-40" />
        <div className="glass-orb glass-orb-rust w-[280px] h-[280px] bottom-0 -left-32" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-3xl">
            <span
              className="text-xs font-semibold tracking-[0.2em] uppercase mb-4 block"
              style={{ color: gold, fontFamily: "var(--font-body)" }}
            >
              BioChain Sourcing - Regenerative &amp; Healing Industries
            </span>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
              style={{ fontFamily: "var(--font-display)" }}
            >
              We Source What <span style={{ color: gold }}>Heals</span> - With
              the Same Rigor We Apply to Billion-Dollar IT Deals
            </h1>
            <p
              className="text-lg leading-relaxed mb-10"
              style={{
                color: "oklch(0.4 0.02 50)",
                fontFamily: "var(--font-body)",
              }}
            >
              Peptides. Exosomes. Stem cells. NAD+. RampRate&apos;s 24-year
              procurement intelligence engine now serves the healing economy -
              verified provenance, assessed quality, and disclosed pricing on
              every transaction.
            </p>
            <div className="flex flex-wrap gap-4 mb-12">
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md text-sm font-semibold text-white transition-all shadow-lg"
                style={{ background: gold, fontFamily: "var(--font-body)" }}
              >
                Request a Sourcing Audit
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
              <a
                href="#supplier-categories"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md text-sm font-semibold border-2 border-black/15 hover:bg-black/5 transition-all"
                style={{ fontFamily: "var(--font-body)" }}
              >
                See What We Source
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Origin Story + Introduction */}
      <section className="relative section-warm overflow-hidden py-16 sm:py-20">
        <div className="glass-orb glass-orb-rust w-[280px] h-[280px] -bottom-32 -left-32" />
        <div className="glass-orb glass-orb-amber w-[300px] h-[300px] -top-32 -right-32" />
        <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8">
          <span
            className="text-xs font-semibold tracking-[0.2em] uppercase"
            style={{ color: gold, fontFamily: "var(--font-body)" }}
          >
            Twenty-Five Years in the Making
          </span>
          <p
            className="mt-6 text-lg leading-relaxed"
            style={{
              color: "oklch(0.35 0.02 50)",
              fontFamily: "var(--font-body)",
            }}
          >
            Twenty-five years ago, RampRate started managing IT supply chains
            for Fortune 500 buyers who cared more about quality, timeliness, and
            fit to their specific needs than about the lowest price. There was
            no way to verify who could actually deliver on those things, so we
            built it: validated pricing, verified track records, and a
            reputational layer the industry didn&apos;t have. That&apos;s the
            business we&apos;re still in.
          </p>
          <p
            className="mt-5 text-lg leading-relaxed"
            style={{
              color: "oklch(0.35 0.02 50)",
              fontFamily: "var(--font-body)",
            }}
          >
            The biologics and regenerative health market has the same problem
            today, plus a fraud problem the IT industry never had at this scale.
            Peptides, exosomes, stem cells: real product moving through supply
            chains nobody has independently verified, priced however the seller
            feels like pricing it. We&apos;re bringing the same discipline here
            that we built in tech. There are no bad suppliers in biochain, only
            bad fits, and our job is to find the right one for every
            transaction, fast enough and cheap enough that doing it right stays
            the more profitable choice, with the savings passed to everyone in
            the deal.
          </p>
          <p
            className="mt-5 text-lg font-semibold leading-relaxed"
            style={{
              color: "oklch(0.22 0.02 50)",
              fontFamily: "var(--font-display)",
            }}
          >
            That&apos;s why we created BioChain.
          </p>
        </div>
      </section>

      {/* Proof Stats Band */}
      <section className="py-12 sm:py-14" style={{ background: "var(--dark)" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {proofStats.map((s) => (
              <Reveal key={s.label} className="text-center">
                <div
                  className="text-2xl sm:text-3xl font-bold"
                  style={{ color: gold, fontFamily: "var(--font-display)" }}
                >
                  {s.value}
                </div>
                <div
                  className="mt-1 text-xs uppercase tracking-wider"
                  style={{
                    color: "rgba(255,255,255,0.5)",
                    fontFamily: "var(--font-body)",
                  }}
                >
                  {s.label}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why BioChain Exists */}
      <section className="relative section-light overflow-hidden py-16 sm:py-20">
        <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8">
          <span
            className="text-xs font-semibold tracking-[0.2em] uppercase"
            style={{ color: gold, fontFamily: "var(--font-body)" }}
          >
            Why BioChain Exists
          </span>
          <h2
            className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight"
            style={{ fontFamily: "var(--font-display)" }}
          >
            No Bad Vendors. Only Bad Fits.
          </h2>
          <p
            className="mt-6 text-base leading-relaxed"
            style={{
              color: "oklch(0.4 0.02 50)",
              fontFamily: "var(--font-body)",
            }}
          >
            BioChain is RampRate&apos;s trusted sourcing and supplier
            intelligence platform, built to bring transparency, confidence, and
            procurement expertise to the life sciences ecosystem. Rather than
            simply listing vendors, we focus on connecting organizations with
            verified suppliers that best match their specific business needs.
          </p>
          <p
            className="mt-5 text-base leading-relaxed"
            style={{
              color: "oklch(0.4 0.02 50)",
              fontFamily: "var(--font-body)",
            }}
          >
            We believe there are no bad vendors - only bad fits. Our role is to
            help organizations identify the right partners through structured
            evaluation, verified sourcing, and decades of procurement expertise.
          </p>
          <p
            className="mt-5 text-base leading-relaxed"
            style={{
              color: "oklch(0.4 0.02 50)",
              fontFamily: "var(--font-body)",
            }}
          >
            By combining supplier validation, procurement intelligence, and
            marketplace transparency, BioChain is building a cleaner, more
            trusted ecosystem where buyers can make informed sourcing decisions
            with confidence.
          </p>
          <p
            className="mt-5 text-base leading-relaxed"
            style={{
              color: "oklch(0.4 0.02 50)",
              fontFamily: "var(--font-body)",
            }}
          >
            Whether you&apos;re looking for a manufacturing partner, laboratory
            services, ingredients, packaging, logistics, technology, or other
            life sciences solutions, BioChain provides a curated marketplace
            backed by RampRate&apos;s proven procurement methodology.
          </p>
        </div>
      </section>

      {/* Our Procurement Expertise (formerly "Supplier Profile") */}
      <section
        className="relative section-warm overflow-hidden py-16 sm:py-20"
        id="procurement-expertise"
      >
        <div className="glass-orb glass-orb-amber w-[300px] h-[300px] -bottom-32 -right-32" />
        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8">
          <span
            className="text-xs font-semibold tracking-[0.2em] uppercase"
            style={{ color: gold, fontFamily: "var(--font-body)" }}
          >
            Our Procurement Expertise
          </span>
          <h2
            className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Not All Suppliers Are Equal. Supplier Strength Is Everything.
          </h2>
          <p
            className="mt-6 text-base leading-relaxed max-w-3xl"
            style={{
              color: "oklch(0.4 0.02 50)",
              fontFamily: "var(--font-body)",
            }}
          >
            At BioChain, we do not simply find you a cheaper supplier. We
            evaluate every supplier against a 12-variable qualification matrix
            and score them on Supplier Strength - a composite measure of how
            much leverage, reliability, and documentation they can deliver to
            your account. The stronger the supplier&apos;s profile relative to
            your account size and specification, the better your outcome. And
            the better the fit between your account and the supplier&apos;s
            growth objectives, the more likely you are to land below-wholesale
            terms.
          </p>

          <Reveal className="mt-10">
            <div
              className="overflow-x-auto rounded-xl border"
              style={{ borderColor: "oklch(0.9 0.01 80)" }}
            >
              <table
                className="w-full text-sm"
                style={{ borderCollapse: "collapse", minWidth: 480 }}
              >
                <thead>
                  <tr
                    className="border-b-2"
                    style={{ borderColor: "oklch(0.9 0.01 80)" }}
                  >
                    <th
                      className="text-left px-5 py-3.5 text-xs font-bold uppercase tracking-wide"
                      style={{
                        color: "oklch(0.5 0.02 50)",
                        fontFamily: "var(--font-mono)",
                      }}
                    >
                      Variable
                    </th>
                    <th
                      className="text-left px-5 py-3.5 text-xs font-bold uppercase tracking-wide"
                      style={{
                        color: "oklch(0.5 0.02 50)",
                        fontFamily: "var(--font-mono)",
                      }}
                    >
                      Weight
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {supplierStrengthVariables.map((v, i, arr) => (
                    <tr
                      key={v.variable}
                      className={i < arr.length - 1 ? "border-b" : ""}
                      style={{ borderColor: "oklch(0.93 0.01 80)" }}
                    >
                      <td
                        className="px-5 py-3.5 align-top"
                        style={{ fontFamily: "var(--font-body)" }}
                      >
                        {v.variable}
                      </td>
                      <td
                        className="px-5 py-3.5 align-top font-semibold"
                        style={{ color: gold, fontFamily: "var(--font-body)" }}
                      >
                        {v.weight}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 gap-6 mt-6">
            <Reveal>
              <div
                className="rounded-xl p-6 border border-black/5"
                style={{ background: "#fff" }}
              >
                <div
                  className="text-2xl font-bold"
                  style={{ color: gold, fontFamily: "var(--font-display)" }}
                >
                  72/100
                </div>
                <div
                  className="text-xs uppercase tracking-wider mt-1"
                  style={{
                    color: "oklch(0.55 0.02 50)",
                    fontFamily: "var(--font-mono)",
                  }}
                >
                  Minimum Qualifying Score to Enter the Network
                </div>
              </div>
            </Reveal>
            <Reveal>
              <div
                className="rounded-xl p-6 border border-black/5"
                style={{ background: "#fff" }}
              >
                <div
                  className="text-2xl font-bold"
                  style={{ color: gold, fontFamily: "var(--font-display)" }}
                >
                  81/100
                </div>
                <div
                  className="text-xs uppercase tracking-wider mt-1"
                  style={{
                    color: "oklch(0.55 0.02 50)",
                    fontFamily: "var(--font-mono)",
                  }}
                >
                  Average Score Across Active BioChain Suppliers
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal className="mt-10">
            <div
              className="rounded-xl p-7 sm:p-8 border-2"
              style={{ borderColor: gold, background: "#fff" }}
            >
              <span
                className="inline-block text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded mb-4"
                style={{
                  background: goldBg,
                  color: gold,
                  fontFamily: "var(--font-mono)",
                }}
              >
                Illustrative Example
              </span>
              <h4
                className="text-lg font-bold mb-1"
                style={{ fontFamily: "var(--font-display)" }}
              >
                {exampleSupplier.name}
              </h4>
              <p
                className="text-xs uppercase tracking-wider mb-5"
                style={{
                  color: "oklch(0.55 0.02 50)",
                  fontFamily: "var(--font-mono)",
                }}
              >
                {exampleSupplier.category}
              </p>
              <div className="grid sm:grid-cols-2 gap-5 mb-5">
                <div>
                  <div
                    className="text-xs uppercase tracking-wider mb-1"
                    style={{
                      color: "oklch(0.55 0.02 50)",
                      fontFamily: "var(--font-mono)",
                    }}
                  >
                    Supplier Strength Score
                  </div>
                  <div
                    className="text-2xl font-bold"
                    style={{ color: gold, fontFamily: "var(--font-display)" }}
                  >
                    {exampleSupplier.score}
                  </div>
                </div>
                <div>
                  <div
                    className="text-xs uppercase tracking-wider mb-1"
                    style={{
                      color: "oklch(0.55 0.02 50)",
                      fontFamily: "var(--font-mono)",
                    }}
                  >
                    Capacity
                  </div>
                  <div
                    className="text-sm font-semibold"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    {exampleSupplier.capacity}
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <div
                    className="text-xs uppercase tracking-wider mb-1"
                    style={{
                      color: "oklch(0.55 0.02 50)",
                      fontFamily: "var(--font-mono)",
                    }}
                  >
                    Certifications
                  </div>
                  <p
                    className="text-sm leading-relaxed"
                    style={{
                      color: "oklch(0.4 0.02 50)",
                      fontFamily: "var(--font-body)",
                    }}
                  >
                    {exampleSupplier.certs}
                  </p>
                </div>
                <div>
                  <div
                    className="text-xs uppercase tracking-wider mb-1"
                    style={{
                      color: "oklch(0.55 0.02 50)",
                      fontFamily: "var(--font-mono)",
                    }}
                  >
                    Regulatory History
                  </div>
                  <p
                    className="text-sm leading-relaxed"
                    style={{
                      color: "oklch(0.4 0.02 50)",
                      fontFamily: "var(--font-body)",
                    }}
                  >
                    {exampleSupplier.history}
                  </p>
                </div>
                <div>
                  <div
                    className="text-xs uppercase tracking-wider mb-1"
                    style={{
                      color: "oklch(0.55 0.02 50)",
                      fontFamily: "var(--font-mono)",
                    }}
                  >
                    Contract Posture
                  </div>
                  <p
                    className="text-sm leading-relaxed"
                    style={{
                      color: "oklch(0.4 0.02 50)",
                      fontFamily: "var(--font-body)",
                    }}
                  >
                    {exampleSupplier.posture}
                  </p>
                </div>
                <div>
                  <div
                    className="text-xs uppercase tracking-wider mb-1"
                    style={{ color: gold, fontFamily: "var(--font-mono)" }}
                  >
                    Why This Supplier Creates Below-Wholesale Opportunity
                  </div>
                  <p
                    className="text-sm leading-relaxed"
                    style={{
                      color: "oklch(0.4 0.02 50)",
                      fontFamily: "var(--font-body)",
                    }}
                  >
                    {exampleSupplier.why}
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* How BioChain Works (formerly "BioChain Process") */}
      <section
        className="relative section-dark overflow-hidden py-16 sm:py-20"
        id="how-it-works"
      >
        <div className="glass-orb glass-orb-amber w-[350px] h-[350px] -top-40 -right-40" />
        <div className="glass-orb glass-orb-rust w-[200px] h-[200px] bottom-20 -left-20" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <span
            className="text-xs font-semibold tracking-[0.2em] uppercase"
            style={{ color: gold, fontFamily: "var(--font-body)" }}
          >
            How BioChain Works
          </span>
          <h2
            className="mt-4 text-3xl sm:text-4xl font-bold text-white max-w-2xl"
            style={{ fontFamily: "var(--font-display)" }}
          >
            From Application to Approved Supplier
          </h2>

          {/* Vetting pipeline visual */}
          <div className="mt-12 flex flex-col sm:flex-row items-stretch gap-3 sm:gap-2">
            {vettingPipeline.map((s, i, arr) => (
              <div key={s.step} className="flex-1 flex items-center gap-2">
                <Reveal className="flex-1 h-full">
                  <div
                    className="h-full rounded-xl p-5 border border-white/10"
                    style={{ background: "rgba(255,255,255,0.05)" }}
                  >
                    <span
                      className="text-xs font-semibold tracking-wider"
                      style={{ color: gold, fontFamily: "var(--font-mono)" }}
                    >
                      {s.step}
                    </span>
                    <h4
                      className="mt-2 text-sm font-bold text-white"
                      style={{ fontFamily: "var(--font-display)" }}
                    >
                      {s.label}
                    </h4>
                    <p
                      className="mt-2 text-xs leading-relaxed text-white/60"
                      style={{ fontFamily: "var(--font-body)" }}
                    >
                      {s.desc}
                    </p>
                  </div>
                </Reveal>
                {i < arr.length - 1 && (
                  <div
                    className="hidden sm:flex items-center justify-center shrink-0"
                    style={{ color: gold }}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Supporting detail cards */}
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((step) => (
              <Reveal key={step.num}>
                <div
                  className="rounded-xl p-6 border border-white/10 h-full"
                  style={{ background: "rgba(255,255,255,0.05)" }}
                >
                  <span
                    className="text-xs font-semibold tracking-wider"
                    style={{ color: gold, fontFamily: "var(--font-mono)" }}
                  >
                    {step.num}
                  </span>
                  <h3
                    className="mt-3 text-lg font-bold text-white"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    {step.title}
                  </h3>
                  <p
                    className="mt-3 text-sm leading-relaxed text-white/60"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    {step.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Supplier Categories (formerly "What We Source" + Product Grid) */}
      <section
        className="relative section-warm overflow-hidden py-16 sm:py-20"
        id="supplier-categories"
      >
        <div className="glass-orb glass-orb-amber w-[300px] h-[300px] -bottom-32 -right-32" />
        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8">
          <span
            className="text-xs font-semibold tracking-[0.2em] uppercase"
            style={{ color: gold, fontFamily: "var(--font-body)" }}
          >
            Supplier Categories
          </span>
          <h2
            className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Every Category - Vetted, Verified, and Ready to Source
          </h2>
          <p
            className="mt-6 text-base leading-relaxed max-w-3xl"
            style={{
              color: "oklch(0.4 0.02 50)",
              fontFamily: "var(--font-body)",
            }}
          >
            We benchmark every category against closed transactions from the
            last 90 days - documented, auditable, and negotiated on your behalf.
            We&apos;re a sourcing partner, not a price list: exact figures are
            shared once we understand your fit, not before.
          </p>
        </div>
      </section>

      <section className="relative section-light overflow-hidden py-16 sm:py-20">
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p) => (
              <Reveal key={p.slug}>
                <div
                  className="rounded-xl p-7 border border-black/5 flex flex-col h-full"
                  style={{ background: "oklch(0.97 0.01 80)" }}
                >
                  <div
                    className="w-11 h-11 rounded-lg flex items-center justify-center mb-5"
                    style={{ background: goldBg, color: gold }}
                  >
                    {productIcons[p.slug]}
                  </div>
                  <h3
                    className="text-lg font-bold mb-3"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    {p.title}
                  </h3>
                  <p
                    className="text-sm leading-relaxed mb-3 flex-1"
                    style={{
                      color: "oklch(0.45 0.02 50)",
                      fontFamily: "var(--font-body)",
                    }}
                  >
                    {p.desc}
                  </p>
                  <div
                    className="text-xs uppercase tracking-wider mb-4"
                    style={{ color: gold, fontFamily: "var(--font-mono)" }}
                  >
                    {p.meta}
                  </div>
                  <div
                    className="rounded-lg border p-4 mb-4"
                    style={{
                      borderColor: "oklch(0.9 0.01 80)",
                      background: "#fff",
                    }}
                  >
                    <div className="flex justify-between items-baseline gap-2 mb-2">
                      <span
                        className="text-xs uppercase tracking-wider"
                        style={{
                          color: "oklch(0.55 0.02 50)",
                          fontFamily: "var(--font-mono)",
                        }}
                      >
                        Street Price
                      </span>
                      <span
                        className="text-sm font-semibold line-through"
                        style={{
                          color: "oklch(0.5 0.02 50)",
                          fontFamily: "var(--font-body)",
                        }}
                      >
                        {p.street}
                      </span>
                    </div>
                    <div
                      className="text-xs font-semibold px-2 py-1.5 rounded text-center"
                      style={{
                        background: "oklch(0.6 0.14 150 / 0.12)",
                        color: "oklch(0.45 0.14 150)",
                        fontFamily: "var(--font-mono)",
                      }}
                    >
                      {p.save}
                    </div>
                  </div>
                  {p.links.length > 0 && (
                    <div className="flex flex-col gap-1.5 mb-4">
                      {p.links.map((l) => (
                        <a
                          key={l.href}
                          href={l.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs"
                          style={{
                            color: "oklch(0.5 0.18 250)",
                            fontFamily: "var(--font-mono)",
                          }}
                        >
                          → {l.label}
                        </a>
                      ))}
                    </div>
                  )}
                  <Link
                    href="/contact"
                    className="text-center text-sm font-bold px-4 py-3.5 rounded-md transition-colors"
                    style={{
                      background: goldBg,
                      color: gold,
                      fontFamily: "var(--font-body)",
                    }}
                  >
                    Source {p.title} with RampRate →
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Minimum Account Callout */}
      <section
        className="relative overflow-hidden py-16 sm:py-20"
        style={{ background: gold }}
      >
        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="rounded-2xl bg-white p-8 sm:p-10 shadow-lg">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-10 mb-6">
                <div>
                  <div
                    className="text-4xl sm:text-5xl font-bold"
                    style={{ color: gold, fontFamily: "var(--font-display)" }}
                  >
                    $50,000
                  </div>
                  <div
                    className="text-xs uppercase tracking-wider mt-1"
                    style={{
                      color: "oklch(0.55 0.02 50)",
                      fontFamily: "var(--font-mono)",
                    }}
                  >
                    Minimum Annual Biologics Spend
                  </div>
                </div>
                <div
                  className="hidden sm:block w-px self-stretch"
                  style={{ background: "oklch(0.9 0.01 80)" }}
                />
                <div className="grid grid-cols-2 gap-6 flex-1">
                  <div>
                    <div
                      className="text-lg font-bold"
                      style={{
                        color: "oklch(0.2 0.02 50)",
                        fontFamily: "var(--font-display)",
                      }}
                    >
                      $75K–$2M+
                    </div>
                    <div
                      className="text-xs uppercase tracking-wider mt-1"
                      style={{
                        color: "oklch(0.55 0.02 50)",
                        fontFamily: "var(--font-mono)",
                      }}
                    >
                      Typical Client Range
                    </div>
                  </div>
                  <div>
                    <div
                      className="text-lg font-bold"
                      style={{
                        color: "oklch(0.2 0.02 50)",
                        fontFamily: "var(--font-display)",
                      }}
                    >
                      35–55%
                    </div>
                    <div
                      className="text-xs uppercase tracking-wider mt-1"
                      style={{
                        color: "oklch(0.55 0.02 50)",
                        fontFamily: "var(--font-mono)",
                      }}
                    >
                      Avg. First-Year Savings
                    </div>
                  </div>
                </div>
              </div>
              <p
                className="text-sm leading-relaxed"
                style={{
                  color: "oklch(0.4 0.02 50)",
                  fontFamily: "var(--font-body)",
                }}
              >
                BioChain is a professional-grade practice built for established
                accounts. We require a minimum of $50,000 in annual biologics
                spend to engage - below that threshold, the savings math
                doesn&apos;t clear our fee structure. At $50K and above, it
                compounds dramatically.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Price-to-Value Table */}
      <section
        className="relative section-warm overflow-hidden py-16 sm:py-20"
        id="value-table"
      >
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <span
            className="text-xs font-semibold tracking-[0.2em] uppercase"
            style={{ color: gold, fontFamily: "var(--font-body)" }}
          >
            Price-to-Value Comparison
          </span>
          <h2
            className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Verified Market Intelligence, By Category
          </h2>
          <p
            className="mt-6 text-base leading-relaxed max-w-3xl mb-10"
            style={{
              color: "oklch(0.4 0.02 50)",
              fontFamily: "var(--font-body)",
            }}
          >
            Based on BioChain SPY Index closed transaction data from the last 90
            days. Quality-adjusted only - if the cheaper option has inferior
            documentation, it is not the benchmark. Exact figures are shared
            once we understand your fit - we&apos;re a sourcing partner, not a
            price list.
          </p>
          <Reveal>
            <div
              className="overflow-x-auto rounded-xl border"
              style={{ borderColor: "oklch(0.88 0.01 80)", background: "#fff" }}
            >
              <table
                className="w-full text-sm"
                style={{ borderCollapse: "collapse", minWidth: 640 }}
              >
                <thead>
                  <tr
                    className="border-b-2"
                    style={{ borderColor: "oklch(0.9 0.01 80)" }}
                  >
                    {[
                      "Product",
                      "Street Price",
                      "Typical Saving",
                      "What Else You Get",
                    ].map((h) => (
                      <th
                        key={h}
                        className="text-left px-5 py-3.5 text-xs font-bold uppercase tracking-wide"
                        style={{
                          color: "oklch(0.5 0.02 50)",
                          fontFamily: "var(--font-mono)",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      product: "BPC-157 · 5mg vial",
                      street: "$35–$75/vial",
                      save: "38–65%+",
                      extra:
                        "HPLC purity report, lot traceability, cold-chain SLA",
                    },
                    {
                      product: "TB-500 · 5mg vial",
                      street: "$40–$80/vial",
                      save: "45–68%+",
                      extra:
                        "Mass spec, sequence confirmation, research-use docs",
                    },
                    {
                      product: "Semaglutide analog · 10mg research",
                      street: "$280–$650/unit",
                      save: "45–70%+",
                      extra:
                        "Synthesis pathway confirmation, import compliance docs",
                    },
                    {
                      product: "MSC Exosomes · 1B particles",
                      street: "$1,200–$3,500/vial",
                      save: "30–55%+",
                      extra:
                        "NTA particle sizing, endotoxin test, donor consent docs",
                    },
                    {
                      product: "Umbilical Cord MSCs · 5M cells",
                      street: "$2,000–$8,000/unit",
                      save: "25–50%+",
                      extra:
                        "Viability guarantee, cryopreservation SLA, full donor chain",
                    },
                    {
                      product: "NMN pharmaceutical grade · 1kg",
                      street: "$800–$2,500/kg",
                      save: "40–60%+",
                      extra: "HPLC purity ≥99%, heavy metals testing",
                    },
                    {
                      product: "NAD+ IV Drip Kit · clinic-grade",
                      street: "$180–$380/kit",
                      save: "45–63%+",
                      extra:
                        "Medical-device-grade consumables, unified supply chain",
                    },
                    {
                      product: "Full BioChain Advisory · ongoing",
                      street: "Standard retainer",
                      save: "2X return",
                      extra:
                        "SPY Index access, supplier negotiation, contract architecture",
                    },
                  ].map((row, i, arr) => (
                    <tr
                      key={row.product}
                      className={i < arr.length - 1 ? "border-b" : ""}
                      style={{ borderColor: "oklch(0.93 0.01 80)" }}
                    >
                      <td
                        className="px-5 py-4 font-bold align-top"
                        style={{ fontFamily: "var(--font-body)" }}
                      >
                        {row.product}
                      </td>
                      <td
                        className="px-5 py-4 align-top"
                        style={{
                          color: "oklch(0.45 0.02 50)",
                          fontFamily: "var(--font-body)",
                        }}
                      >
                        {row.street}
                      </td>
                      <td className="px-5 py-4 align-top">
                        <span
                          className="text-xs font-semibold px-2 py-0.5 rounded"
                          style={{
                            background: "oklch(0.6 0.14 150 / 0.12)",
                            color: "oklch(0.45 0.14 150)",
                            fontFamily: "var(--font-mono)",
                          }}
                        >
                          {row.save}
                        </span>
                      </td>
                      <td
                        className="px-5 py-4 align-top"
                        style={{
                          color: "oklch(0.45 0.02 50)",
                          fontFamily: "var(--font-body)",
                        }}
                      >
                        {row.extra}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
          <p
            className="mt-4 text-xs leading-relaxed"
            style={{
              color: "oklch(0.55 0.02 50)",
              fontFamily: "var(--font-mono)",
            }}
          >
            Savings percentages reflect BioChain SPY Index closed transaction
            data. Street prices verified from current retail and direct-channel
            sources as of Q2 2026. The + on every saving reflects that
            RampRate&apos;s supplier partnerships frequently produce pricing
            below published wholesale depending on account fit, volume
            commitment, and supplier strategic alignment. All accounts require a
            minimum of $50,000 in annual biologics spend. RampRate does not
            publish target prices - the sourcing audit produces your actual
            benchmark.
          </p>

          <Reveal className="mt-14">
            <div
              className="rounded-2xl p-8 sm:p-10 border"
              style={{ borderColor: "oklch(0.88 0.01 80)", background: "#fff" }}
            >
              <h3
                className="text-xl sm:text-2xl font-bold mb-4"
                style={{ fontFamily: "var(--font-display)" }}
              >
                Why Some of Our Clients Pay Less Than Wholesale
              </h3>
              <p
                className="text-sm leading-relaxed mb-6 max-w-3xl"
                style={{
                  color: "oklch(0.4 0.02 50)",
                  fontFamily: "var(--font-body)",
                }}
              >
                Most procurement advisors negotiate against the street price.
                RampRate negotiates against the supplier&apos;s cost structure.
                After 24 years of directing volume at the manufacturers and labs
                that supply the supply chain, we have relationships that exist
                below the distributor tier entirely. When the fit is right -
                meaning your account volume, specification consistency, and
                payment terms align with what a supplier needs to grow - we can
                structure agreements that put you at or below the price a
                regional distributor pays. The fit determines the outcome. The
                sourcing audit determines the fit.
              </p>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md text-sm font-semibold text-white transition-all shadow-lg"
                style={{ background: gold, fontFamily: "var(--font-body)" }}
              >
                Request Your Sourcing Audit
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Client Profile */}
      <section
        className="relative section-warm overflow-hidden py-16 sm:py-20"
        id="client-profile"
      >
        <div className="glass-orb glass-orb-amber w-[300px] h-[300px] -bottom-32 -right-32" />
        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8">
          <span
            className="text-xs font-semibold tracking-[0.2em] uppercase"
            style={{ color: gold, fontFamily: "var(--font-body)" }}
          >
            Who BioChain Is Built For
          </span>
          <h2
            className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight"
            style={{ fontFamily: "var(--font-display)" }}
          >
            The Accounts That Get Maximum Value From BioChain
          </h2>
          <p
            className="mt-6 text-base leading-relaxed max-w-3xl"
            style={{
              color: "oklch(0.4 0.02 50)",
              fontFamily: "var(--font-body)",
            }}
          >
            Not every buyer is the right fit. We do our best work with
            organizations that have meaningful scale, documented quality
            requirements, and a commitment to verified sourcing over
            lowest-price guessing.
          </p>

          <Reveal className="mt-10">
            <div className="rounded-xl p-7 border border-black/5 bg-white">
              <h3
                className="text-sm font-bold uppercase tracking-wider mb-5"
                style={{ color: gold, fontFamily: "var(--font-mono)" }}
              >
                Ideal Client Characteristics
              </h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {clientCharacteristics.map((c) => (
                  <div key={c} className="flex items-start gap-3">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={gold}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="shrink-0 mt-0.5"
                    >
                      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    <span
                      className="text-sm leading-relaxed"
                      style={{
                        color: "oklch(0.4 0.02 50)",
                        fontFamily: "var(--font-body)",
                      }}
                    >
                      {c}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal className="mt-6">
            <div className="flex flex-wrap gap-2">
              {clientOrgTypes.map((org) => (
                <span
                  key={org}
                  className="text-xs font-semibold px-3 py-1.5 rounded-full border"
                  style={{
                    borderColor: "oklch(0.85 0.02 80)",
                    color: "oklch(0.4 0.02 50)",
                    fontFamily: "var(--font-mono)",
                  }}
                >
                  {org}
                </span>
              ))}
            </div>
          </Reveal>

          <Reveal className="mt-10">
            <div
              className="rounded-xl p-7 sm:p-8 border-2"
              style={{ borderColor: gold, background: "#fff" }}
            >
              <span
                className="inline-block text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded mb-4"
                style={{
                  background: goldBg,
                  color: gold,
                  fontFamily: "var(--font-mono)",
                }}
              >
                Illustrative Example
              </span>
              <h4
                className="text-lg font-bold mb-1"
                style={{ fontFamily: "var(--font-display)" }}
              >
                {exampleClient.account}
              </h4>
              <p
                className="text-xs uppercase tracking-wider mb-5"
                style={{
                  color: "oklch(0.55 0.02 50)",
                  fontFamily: "var(--font-mono)",
                }}
              >
                {exampleClient.type}
              </p>
              <div className="grid sm:grid-cols-2 gap-5 mb-5">
                <div>
                  <div
                    className="text-xs uppercase tracking-wider mb-1"
                    style={{
                      color: "oklch(0.55 0.02 50)",
                      fontFamily: "var(--font-mono)",
                    }}
                  >
                    Annual Spend at Engagement
                  </div>
                  <div
                    className="text-sm font-semibold"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    {exampleClient.spend}
                  </div>
                </div>
                <div>
                  <div
                    className="text-xs uppercase tracking-wider mb-1"
                    style={{
                      color: "oklch(0.55 0.02 50)",
                      fontFamily: "var(--font-mono)",
                    }}
                  >
                    Primary Categories
                  </div>
                  <div
                    className="text-sm font-semibold"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    {exampleClient.categories}
                  </div>
                </div>
              </div>
              <div className="mb-5">
                <div
                  className="text-xs uppercase tracking-wider mb-1"
                  style={{
                    color: "oklch(0.55 0.02 50)",
                    fontFamily: "var(--font-mono)",
                  }}
                >
                  Problem
                </div>
                <p
                  className="text-sm leading-relaxed"
                  style={{
                    color: "oklch(0.4 0.02 50)",
                    fontFamily: "var(--font-body)",
                  }}
                >
                  {exampleClient.problem}
                </p>
              </div>
              <div>
                <div
                  className="text-xs uppercase tracking-wider mb-1"
                  style={{ color: gold, fontFamily: "var(--font-mono)" }}
                >
                  BioChain Outcome - Year One
                </div>
                <p
                  className="text-sm leading-relaxed"
                  style={{
                    color: "oklch(0.4 0.02 50)",
                    fontFamily: "var(--font-body)",
                  }}
                >
                  {exampleClient.outcome}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Blog Rail */}
      <section
        className="relative section-warm overflow-hidden py-16 sm:py-20"
        id="blog"
      >
        <div className="glass-orb glass-orb-amber w-[300px] h-[300px] -bottom-32 -right-32" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <span
            className="text-xs font-semibold tracking-[0.2em] uppercase"
            style={{ color: gold, fontFamily: "var(--font-body)" }}
          >
            From tonygreenberg.com
          </span>
          <h2
            className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl"
            style={{ fontFamily: "var(--font-display)" }}
          >
            The Thinking Behind the Supply Chain
          </h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {blogPosts.map((post) => (
              <Reveal key={post.href}>
                <a
                  href={post.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block h-full rounded-xl overflow-hidden border border-black/5 bg-white hover:shadow-md transition-shadow"
                >
                  <div className="relative w-full h-40 bg-black/5">
                    <Image
                      src={post.img}
                      alt={post.title}
                      fill
                      sizes="(max-width: 768px) 100vw, 25vw"
                      className="object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <span
                      className="text-xs font-semibold uppercase tracking-wider"
                      style={{ color: gold, fontFamily: "var(--font-mono)" }}
                    >
                      {post.tag}
                    </span>
                    <h3
                      className="mt-3 text-base font-bold leading-snug"
                      style={{ fontFamily: "var(--font-display)" }}
                    >
                      {post.title}
                    </h3>
                    <p
                      className="mt-3 text-sm leading-relaxed"
                      style={{
                        color: "oklch(0.45 0.02 50)",
                        fontFamily: "var(--font-body)",
                      }}
                    >
                      {post.excerpt}
                    </p>
                  </div>
                </a>
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-12">
            <a
              href="https://tonygreenberg.com/blog"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md text-sm font-semibold border-2 transition-all"
              style={{
                borderColor: "oklch(0.8 0.02 80)",
                fontFamily: "var(--font-body)",
              }}
            >
              Read All Essays at tonygreenberg.com →
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        className="relative section-light overflow-hidden py-16 sm:py-20"
        id="faq"
      >
        <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8">
          <span
            className="text-xs font-semibold tracking-[0.2em] uppercase"
            style={{ color: gold, fontFamily: "var(--font-body)" }}
          >
            What We Hear Every Week
          </span>
          <h2
            className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight"
            style={{ fontFamily: "var(--font-display)" }}
          >
            The Questions Clinic Directors Ask First
          </h2>
          <div className="mt-10">
            {faqs.map((f) => (
              <div
                key={f.num}
                className="border-b"
                style={{ borderColor: "oklch(0.9 0.01 80)" }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === f.num ? null : f.num)}
                  className="w-full flex items-start gap-4 py-6 text-left"
                >
                  <span
                    className="text-xs font-semibold pt-0.5"
                    style={{ color: gold, fontFamily: "var(--font-mono)" }}
                  >
                    {f.num}
                  </span>
                  <span
                    className="flex-1 text-base font-bold"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    {f.question}
                  </span>
                  <span
                    className="text-xl font-bold shrink-0 transition-transform"
                    style={{
                      color: gold,
                      transform: openFaq === f.num ? "rotate(45deg)" : "none",
                    }}
                  >
                    +
                  </span>
                </button>
                {openFaq === f.num && (
                  <p
                    className="pb-6 pl-8 text-sm leading-relaxed"
                    style={{
                      color: "oklch(0.45 0.02 50)",
                      fontFamily: "var(--font-body)",
                    }}
                  >
                    {f.answer}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ImpactSoul cross-link */}
      <section
        className="relative section-warm overflow-hidden py-16 sm:py-20"
        id="impactsoul"
      >
        <div className="glass-orb glass-orb-rust w-[300px] h-[300px] -bottom-32 -right-32" />
        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8">
          <span
            className="text-xs font-semibold tracking-[0.2em] uppercase"
            style={{ color: gold, fontFamily: "var(--font-body)" }}
          >
            ImpactSoul Connection
          </span>
          <h2
            className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Regenerative Capital Meets Regenerative Medicine
          </h2>
          <p
            className="mt-6 text-base leading-relaxed max-w-2xl mb-8"
            style={{
              color: "oklch(0.4 0.02 50)",
              fontFamily: "var(--font-body)",
            }}
          >
            BioChain Sourcing is the supply chain infrastructure for what
            ImpactSoul builds on the capital side. Verified, ethically sourced
            supply chains are the trust layer that connects regenerative
            medicine providers to impact-aligned capital.
          </p>
          <Link
            href="/impactsoul"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md text-sm font-semibold text-white transition-all shadow-lg"
            style={{ background: gold, fontFamily: "var(--font-body)" }}
          >
            Explore ImpactSoul
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </section>

      {/* Testimonials */}
      <section
        className="relative section-light overflow-hidden py-16 sm:py-20 border-y"
        style={{
          background: "oklch(0.96 0.015 80)",
          borderColor: "oklch(0.9 0.01 80)",
        }}
      >
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-14">
            <span
              className="text-xs font-semibold tracking-[0.2em] uppercase"
              style={{ color: gold, fontFamily: "var(--font-body)" }}
            >
              Client Results
            </span>
            <h2
              className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight"
              style={{ fontFamily: "var(--font-display)" }}
            >
              What Clinics and Practices Say
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <Reveal key={t.attr}>
                <div
                  className="rounded-xl p-7 border h-full bg-white"
                  style={{ borderColor: "oklch(0.9 0.01 80)" }}
                >
                  <div
                    className="text-sm mb-3 tracking-wider"
                    style={{ color: gold }}
                    aria-hidden="true"
                  >
                    ★★★★★
                  </div>
                  <p
                    className="text-sm leading-relaxed mb-4"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <div
                    className="text-xs uppercase tracking-wider"
                    style={{
                      color: "oklch(0.55 0.02 50)",
                      fontFamily: "var(--font-mono)",
                    }}
                  >
                    {t.attr}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTAs - Two Paths: Sourcing vs. Supplying */}
      <section className="relative section-warm overflow-hidden py-16 sm:py-20">
        <div className="glass-orb glass-orb-amber w-[300px] h-[300px] -top-32 -left-32" />
        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="text-center mb-14">
              <span
                className="text-xs font-semibold tracking-[0.2em] uppercase"
                style={{ color: gold, fontFamily: "var(--font-body)" }}
              >
                Get Involved
              </span>
              <h2
                className="text-3xl sm:text-4xl font-bold mt-3"
                style={{ fontFamily: "var(--font-display)" }}
              >
                Two Ways to Join the BioChain Network
              </h2>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-6">
            <Reveal>
              <div
                className="rounded-xl p-8 border border-black/5 flex flex-col h-full"
                style={{ background: "#fff" }}
              >
                <div
                  className="w-11 h-11 rounded-lg flex items-center justify-center mb-5"
                  style={{ background: goldBg, color: gold }}
                >
                  <LuHandshake size={20} />
                </div>
                <h3
                  className="text-lg font-bold mb-3"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  Sourcing Peptides?
                </h3>
                <p
                  className="text-sm leading-relaxed mb-6 flex-1"
                  style={{
                    color: "oklch(0.45 0.02 50)",
                    fontFamily: "var(--font-body)",
                  }}
                >
                  Clinics, distributors, and healthcare organizations sourcing
                  peptides, exosomes, stem cells, or NAD+ - apply to scope a
                  free BioChain sourcing audit. No fee, no obligation,
                  confidential.
                </p>
                <Link
                  href="/biochain/buyer-intake"
                  className="inline-flex items-center justify-center gap-2 text-sm font-bold px-4 py-3.5 rounded-md transition-colors"
                  style={{
                    background: goldBg,
                    color: gold,
                    fontFamily: "var(--font-body)",
                  }}
                >
                  Apply as a Buyer
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </Reveal>
            <Reveal>
              <div
                className="rounded-xl p-8 border border-black/5 flex flex-col h-full"
                style={{ background: "#fff" }}
              >
                <div
                  className="w-11 h-11 rounded-lg flex items-center justify-center mb-5"
                  style={{ background: goldBg, color: gold }}
                >
                  <LuFactory size={20} />
                </div>
                <h3
                  className="text-lg font-bold mb-3"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  Supplying Peptides?
                </h3>
                <p
                  className="text-sm leading-relaxed mb-6 flex-1"
                  style={{
                    color: "oklch(0.45 0.02 50)",
                    fontFamily: "var(--font-body)",
                  }}
                >
                  Peptide manufacturers, research peptide suppliers, and
                  compounded pharmacy producers - apply for RampRate&apos;s
                  verified supplier network. Quick first-touch application, no
                  uploads, about 2-3 minutes.
                </p>
                <Link
                  href="/biochain/supplier-intake"
                  className="inline-flex items-center justify-center gap-2 text-sm font-bold px-4 py-3.5 rounded-md transition-colors"
                  style={{
                    background: gold,
                    color: "#fff",
                    fontFamily: "var(--font-body)",
                  }}
                >
                  Apply as a Supplier
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="text-center mt-10">
            <Link
              href="/biochain/catalogue-access"
              className="text-sm font-semibold underline underline-offset-4"
              style={{ color: gold, fontFamily: "var(--font-body)" }}
            >
              Browse our full product catalog first →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA / Guarantee */}
      <section className="py-16 sm:py-20" style={{ background: gold }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <h2
            className="text-3xl sm:text-4xl font-bold text-white mb-4"
            style={{ fontFamily: "var(--font-display)" }}
          >
            2X Return or You Don&apos;t Pay
          </h2>
          <p
            className="text-white/85 text-base sm:text-lg leading-relaxed mb-8 max-w-2xl mx-auto"
            style={{ fontFamily: "var(--font-body)" }}
          >
            The sourcing audit is always free. If we don&apos;t deliver a 2X
            return on our BioChain sourcing engagements, you don&apos;t pay.
          </p>
          <Link
            href="/contact"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md text-sm font-semibold bg-white hover:bg-white/90 transition-all shadow-lg"
            style={{ color: gold, fontFamily: "var(--font-body)" }}
          >
            Start a Sourcing Conversation
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </section>
    </main>
  );
}
